import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './Agenda.css';

interface Event {
  id: string;
  date: Date;
  title: string;
  description: string;
  type: string;
  lieu: string;
}

interface Loge {
  nom: string;
  jour: string;
  date: string;
}

function Agenda() {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState<Event[]>([]);
  const [calendar, setCalendar] = useState<(number | null)[][]>([]);
  const [loges, setLoges] = useState<Loge[]>([]);
  
  // Génération dynamique des années en fonction de l'année actuelle
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 4; // 4 ans en arrière
    const endYear = currentYear + 4;   // 4 ans en avant
    const yearRange = endYear - startYear + 1;
    return Array.from({ length: yearRange }, (_, i) => startYear + i);
  };

  const years = generateYears();
  
  const months = [
    "JANVIER", "FÉVRIER", "MARS", "AVRIL", "MAI", "JUIN",
    "JUILLET", "AOÛT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DÉCEMBRE"
  ];

  const weekDays = ["LUNDI", "MARDI", "MERCREDI", "JEUDI", "VENDREDI", "SAMEDI", "DIMANCHE"];

  // Récupérer les loges
  useEffect(() => {
    const fetchLoges = async () => {
      try {
        const db = getFirestore();
        const logesSnapshot = await getDocs(collection(db, 'Loges'));
        const logesData = logesSnapshot.docs.map(doc => ({
          nom: doc.id,
          jour: doc.data().jour || '',
          date: doc.data().date || '',
        }));
        setLoges(logesData);
      } catch (error) {
        console.error('Erreur lors de la récupération des loges:', error);
      }
    };

    fetchLoges();
  }, []);

  useEffect(() => {
    const generateCalendar = () => {
      const firstDay = new Date(selectedYear, selectedMonth, 1);
      const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
      
      // Ajuster le jour de la semaine (0 = dimanche, nous voulons lundi = 0)
      let startingDay = firstDay.getDay() - 1;
      if (startingDay === -1) startingDay = 6; // Si c'est dimanche (0), on le met à 6

      const totalDays = lastDay.getDate();
      const weeks = Math.ceil((totalDays + startingDay) / 7);
      
      const calendarArray = Array(weeks).fill(null).map(() => Array(7).fill(null));
      
      let currentDay = 1;
      for (let week = 0; week < weeks; week++) {
        for (let day = 0; day < 7; day++) {
          if (week === 0 && day < startingDay) {
            continue;
          }
          if (currentDay > totalDays) {
            break;
          }
          calendarArray[week][day] = currentDay++;
        }
      }
      
      setCalendar(calendarArray);
    };

    generateCalendar();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const db = getFirestore();
        const startOfMonth = new Date(selectedYear, selectedMonth, 1);
        const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0);

        const eventsQuery = query(
          collection(db, 'Events'),
          where('date', '>=', startOfMonth),
          where('date', '<=', endOfMonth)
        );

        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData = eventsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date.toDate()
        })) as Event[];

        setEvents(eventsData);
      } catch (error) {
        console.error('Erreur lors de la récupération des événements:', error);
      }
    };

    fetchEvents();
  }, [selectedMonth, selectedYear]);

  // Fonction pour trouver le N-ième jour de la semaine dans le mois
  const findNthWeekday = (n: number, weekday: number, date: Date) => {
    let count = 0;
    let current = new Date(date.getFullYear(), date.getMonth(), 1);
    
    while (current.getMonth() === date.getMonth()) {
      if (current.getDay() === weekday) {
        count++;
        if (count === n) {
          return current.getDate();
        }
      }
      current.setDate(current.getDate() + 1);
    }
    return null;
  };

  // Fonction pour convertir le jour en nombre (0-6)
  const jourToNumber = (jour: string): number => {
    const jours = ["DIMANCHE", "LUNDI", "MARDI", "MERCREDI", "JEUDI", "VENDREDI", "SAMEDI"];
    return jours.indexOf(jour.toUpperCase());
  };

  // Fonction pour convertir la date en nombre (1-5)
  const dateToNumber = (date: string): number => {
    const nombres = ["1er", "2eme", "3eme", "4eme", "5eme"];
    return nombres.indexOf(date) + 1;
  };

  // Fonction pour trouver les loges pour un jour donné
  const getLogesForDay = (day: number | null) => {
    if (!day) return [];

    return loges.filter(loge => {
      const jourNum = jourToNumber(loge.jour);
      const dateNum = dateToNumber(loge.date);
      if (jourNum === -1 || dateNum === -1) return false;

      const targetDate = findNthWeekday(dateNum, jourNum, new Date(selectedYear, selectedMonth, 1));
      return targetDate === day;
    });
  };

  // Fonction pour naviguer vers les événements
  const handleEventClick = (eventId: string) => {
    navigate(`/mapage/evenements/${eventId}`);
  };

  return (
    <div className="agenda-container">
      <div className="agenda-header">
        <div className="date-selectors">
          <div className="selector-group">
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="month-selector"
            >
              {months.map((month, index) => (
                <option key={index} value={index}>{month}</option>
              ))}
            </select>
          </div>

          <div className="selector-group">
            <select 
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="year-selector"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="calendar">
        <div className="month-title">
          {months[selectedMonth]} {selectedYear}
        </div>
        <table className="calendar-table">
          <thead>
            <tr className="weekdays-header">
              <th className="week-number-header">N°</th>
              {weekDays.map(day => (
                <th key={day}>{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calendar.map((week, weekIndex) => (
              <tr key={weekIndex}>
                <td className="week-number">{weekIndex + 1}</td>
                {week.map((day, dayIndex) => (
                  <td 
                    key={dayIndex} 
                    className={`calendar-day ${day === null ? 'empty' : ''} ${dayIndex === 6 ? 'sunday' : ''}`}
                  >
                    {day !== null && (
                      <div className="day-content">
                        <span className="day-number">{day}</span>
                        {getLogesForDay(day).map((loge, index) => (
                          <div key={index} className="loge-event">
                            {loge.nom}
                          </div>
                        ))}
                        {events
                          .filter(event => event.date.getDate() === day)
                          .map(event => (
                            <div 
                              key={event.id} 
                              className="event-indicator"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEventClick(event.id);
                              }}
                            >
                              {event.title}
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Agenda;